import { useState } from 'react'
import './cameraGroupTable.scss'
import { CameraGroupTableInitialization } from './CameraGroupTableInitialization'
import BulkApplyModal from '../BulkApplyModal/BulkApplyModal'
import ImportModal from '../ImportModal/ImportModal'
import { SiteDesignGetPayload } from '../../../lib/types'
import Table from '../../../components/Table/Table'
import ModalPopup from '../../../components/ModalPopup/ModalPopup'
import { ButtonStyles, ButtonSize, ModalPopupSize } from '../../../lib/enums'
import Button from '../../../components/Button/Button'
import ImageComplexityWizardModal from '../ImageComplexityWizard/ImageComplexityWizardModal'
import { Row } from '@tanstack/react-table'
import { ComplexityLevel } from '../ImageComplexityWizard/imageComplexityScenarios'

const CameraGroupTable = ({
  siteDesignPayload,
  modifiedCameraRecordings,
  setModifiedCameraRecordings
}: CameraGroupTableProps) => {
  const [showComplexityWizard, setShowComplexityWizard] = useState(false)
  const [complexityWizardStreamId, setComplexityWizardStreamId] = useState<number | null>(null)

  const handleOpenImageComplexityWizard = (row: Row<any>) => {
    setComplexityWizardStreamId(row.original?.streamId ?? null)
    setShowComplexityWizard(true)
  }

  const {
    cameraRecordings,
    addCameraGroup,
    handleDeleteCameraGroup,
    handleModalConfirmDeletion,
    handleModalCancelCameraGroupDeletion,
    showDeleteModal,
    handleDuplicateCameraGroupTableRow,
    cameraGroupTableColumns,
    setShowBulkApplyModal,
    showBulkApplyModal,
    applyBulkSettings,
    setShowImportModal,
    showImportModal,
    handleImport,
    handleImportFile,
    handleDownloadTemplate,
    applyImageComplexityScenarioSelection,
    showSubRow,
    renderSubRow
  } = CameraGroupTableInitialization(
    siteDesignPayload,
    modifiedCameraRecordings,
    setModifiedCameraRecordings,
    handleOpenImageComplexityWizard
  )

  const handleComplexityWizardConfirm = async (selectedScenarioId: string, _selectedComplexity: ComplexityLevel) => {
    if (complexityWizardStreamId === null) return

    const didApplySelection = await applyImageComplexityScenarioSelection(complexityWizardStreamId, selectedScenarioId)
    if (!didApplySelection) return

    setComplexityWizardStreamId(null)
    setShowComplexityWizard(false)
  }

  return (
    <>
      <div className="xsd-camera-group-table">
        {cameraRecordings && (
          <Table
            disableSearch={true}
            disableSort={true}
            data={cameraRecordings}
            columns={cameraGroupTableColumns}
            addButtonLabel="Add Camera Group"
            addButtonLabelIcon="plus"
            addButtonSize={ButtonSize.Small}
            onAddButtonClick={addCameraGroup}
            handleDeleteDataRow={handleDeleteCameraGroup}
            handleDuplicateRow={handleDuplicateCameraGroupTableRow}
            handleShowSubRow={showSubRow}
            renderSubRow={renderSubRow}
            customButtons={[
              <Button
                id={'xsd__e2e-bulk-apply-setting-button'}
                key="bulk-settings"
                label="Bulk Apply Setting"
                size={ButtonSize.Small}
                onClickHandler={() => {
                  setShowBulkApplyModal(true)
                }}
              />,
              <Button
                id={'xsd__e2e-import'}
                key="import"
                label="Import Camera Groups"
                size={ButtonSize.Small}
                onClickHandler={handleImport}
              />
            ]}
          />
        )}
      </div>
      <ModalPopup
        headline="Confirm Delete"
        description="Are you sure you want to delete this row?"
        size={ModalPopupSize.Small}
        showModal={showDeleteModal}
        onCloseButtonclick={handleModalCancelCameraGroupDeletion}
        button1Style={ButtonStyles.Danger}
        button1Label="Yes, delete"
        button1OnClickHandler={handleModalConfirmDeletion}
        button2Style={ButtonStyles.Tertiary}
        button2Label="Cancel"
        button2OnClickHandler={handleModalCancelCameraGroupDeletion}
      />
      <BulkApplyModal
        displayModal={setShowBulkApplyModal}
        isModalDisplayed={showBulkApplyModal}
        siteDesignPayload={siteDesignPayload}
        applyBulkSettings={applyBulkSettings}
      />
      <ImportModal
        displayModal={setShowImportModal}
        isModalDisplayed={showImportModal}
        handleImportFile={handleImportFile}
        handleDownloadTemplate={handleDownloadTemplate}
      />
      <ImageComplexityWizardModal
        show={showComplexityWizard}
        onCancel={() => {
          setComplexityWizardStreamId(null)
          setShowComplexityWizard(false)
        }}
        onConfirm={handleComplexityWizardConfirm}
      />
    </>
  )
}

type CameraGroupTableProps = {
  siteDesignPayload: SiteDesignGetPayload
  modifiedCameraRecordings?
  setModifiedCameraRecordings?
}

export default CameraGroupTable
